import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";
import { FeedCard } from "@/components/FeedCard";
import { Spinner } from "@/components/Common";

export const FULL_H = "h-[calc(100dvh-var(--top-h)-var(--nav-h))]";

// The feed is the only scroller on its page: its height fills exactly the space between top bar and bottom nav,
// so the window itself never scrolls underneath it (that double scroll is what got stuck on iOS).
export function Feed({ params, heightClass = FULL_H }: { params: Record<string, string>; heightClass?: string }) {
  const [items, setItems] = useState<any[]>([]);
  const [next, setNext] = useState<number | null>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(true);
  const sentinel = useRef<HTMLDivElement>(null);
  const key = JSON.stringify(params);

  const reqId = useRef(0);
  const load = useCallback(async (page: number, reset = false) => {
    const my = ++reqId.current;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/feed", { params: { ...params, page } });
      if (my !== reqId.current) return;
      setItems((prev) => {
        const base = reset ? [] : prev;
        const seen = new Set(base.map((x) => x.id));
        return [...base, ...data.items.filter((x: any) => !seen.has(x.id))];
      });
      setNext(data.next_page);
    } catch (e) {
      if (my === reqId.current) setError(errMsg(e));
    } finally {
      if (my === reqId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { setItems([]); setNext(0); load(0, true); }, [load]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting && next !== null && !loading) load(next); }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [next, loading, load]);

  return (
    <div className={`${heightClass} overflow-y-scroll overscroll-contain snap-y snap-mandatory no-scrollbar bg-[hsl(168_20%_12%)] text-white md:rounded-[1.75rem] md:my-3 md:mr-3 md:h-[calc(100dvh-1.5rem)]`} data-testid="feed-container">
      {items.map((it) => (
        <div key={it.id} className="h-full"><FeedCard item={it} muted={muted} onMute={() => setMuted((m) => !m)} onHide={(id: string) => setItems((x) => x.filter((i) => i.id !== id))} /></div>
      ))}
      {!loading && items.length === 0 && !error && (
        <div className="h-full flex flex-col items-start justify-center px-8 gap-6 max-w-lg text-white" data-testid="feed-empty">
          <div className="label text-white/70">Nessun contenuto</div>
          <p className="font-display text-2xl font-bold tracking-tight">{t("feed.empty")}</p>
          <Link to="/pubblica" className="btn btn-primary" data-testid="feed-empty-publish-btn">Pubblica il primo contenuto</Link>
        </div>
      )}
      {error && (
        <div className="h-full flex flex-col items-start justify-center px-8 gap-4 text-white" data-testid="feed-error">
          <p className="text-white/80">{error}</p>
          <button className="btn btn-ghost" onClick={() => load(next ?? 0)} data-testid="feed-retry-btn">Riprova</button>
        </div>
      )}
      <div ref={sentinel} className="h-2" />
      {loading && <div className="flex justify-center py-6"><Spinner /></div>}
    </div>
  );
}
